import { Close, Search } from '@mui/icons-material';
import { Box, InputAdornment, InputBase, styled, type OutlinedInputProps } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { IconButton } from '~/components/IconButton';

const Root = styled('div')(({ theme }) => ({
  opacity: '1',
  '& .MuiInputBase-root': {
    width: '100%',
    backgroundColor: theme.palette.background.field,
    padding: theme.spacing(0.5, 0.75, 0.5, 1.5),
    borderRadius: theme.spacing(0.5),
    fontSize: 'medium',
    fontWeight: 500,
    // MUI defaults the placeholder to currentColor at 0.42 opacity, which drifts from
    // text.secondary. Set it explicitly so it matches the adjacent filter labels.
    '& input::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0.5, 0.5, 0.5, 1),
    },
  },
}));

export interface SearchBarProps
  extends Omit<OutlinedInputProps, 'onChange' | 'onKeyPress' | 'startAdornment'> {
  search: string;
  /** If you set this, it won't push to the url/history */
  onChange?: (search: string) => void;
}

export const SearchBar = ({ onChange, search, ...props }: SearchBarProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(search);

  useEffect(() => setSearchQuery(search), [search, setSearchQuery]);

  const handleChange = onChange ?? ((value: string) => setSearchParams({ search: value }));

  const handleClear = () => {
    setSearchQuery('');
    handleChange('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const input = (
    <Box sx={{ display: 'flex' }}>
      <InputBase
        inputRef={inputRef}
        endAdornment={
          <InputAdornment position="end">
            <IconButton label={t('common.actions.clear_search')} onClick={handleClear} size="small">
              <Close fontSize="small" />
            </IconButton>
          </InputAdornment>
        }
        startAdornment={
          <InputAdornment position="start">
            <Search color="disabled" />
          </InputAdornment>
        }
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
        onKeyDown={(event) => {
          const trimmed = searchQuery.trim();
          if (trimmed === search || event.key !== 'Enter') return;
          handleChange(trimmed);
        }}
        {...props}
        // The placeholder is the only visible label, so it names the input too.
        inputProps={{ 'aria-label': props.placeholder, ...props.inputProps }}
      />
    </Box>
  );

  return <Root>{input}</Root>;
};
